import React, { Component, FC, ReactNode } from "react";
import { Notifier } from "@airbrake/browser";
import { useAppSelector } from "config/hooks";
import { domain, prodURL } from "config/constants";
import Loading from "utils/Loading";
import {
  isChunkLoadError,
  reloadOnceForChunkError,
} from "utils/chunkErrorRecovery";

// ts gives an error that this will always be false
// this is the case if we're working in staging
// however our domain can be updated in our constants when we're developing
// TODO: this will probably change when we add in ENV variables
// @ts-ignore
const currentEnv = domain === prodURL ? "production" : "staging";

type ErrorBoundaryClassProps = {
  children: ReactNode;
  currentUser: { email: string; id: number };
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: unknown;
  reloading: boolean;
};

export const airbrake = new Notifier({
  projectId: 306673,
  projectKey: "aa0396a028f5e8bfd865da7cdc23c89d",
  environment: currentEnv,
});

/**
 * react doesn't yet have static getDerivedStateFromError() or componentDidCatch() hooks
 * so we're sticking with class component from the time being
 */

class ErrorBoundaryClass extends Component<ErrorBoundaryClassProps, ErrorBoundaryState> {
  airbrake: Notifier;
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null, reloading: false };
    this.airbrake = airbrake;
  }

  static getDerivedStateFromError(error: any) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  /**
   *  Production and development builds of React slightly differ in the way componentDidCatch() handles errors.
   *
   *  On development, the errors will bubble up to window,
   *  this means that any window.onerror or window.addEventListener('error', callback) will intercept
   *  the errors that have been caught by componentDidCatch().
   *
   *  On production, instead, the errors will not bubble up,
   *  this means any ancestor error handler will receive only errors not explicitly caught by componentDidCatch().
   */

  componentDidCatch(error: any, info: any) {
    const chunkError = isChunkLoadError(error);
    if (chunkError && reloadOnceForChunkError()) {
      // Stale chunk after a deploy and a reload is in flight — show the
      // spinner and skip Airbrake (auto-recovered, not a bug).
      this.setState({ reloading: true });
      return;
    }
    // Send error to Airbrake
    // note the notify error and params doesn't properly get sent in dev mode
    // TODO: need to test out in prod mode
    this.airbrake.notify({
      error: error,
      params: {
        info: info,
        chunkError,
        user: {
          email: this.props.currentUser?.email,
          id: this.props.currentUser?.id,
        },
      },
    });
  }

  render() {
    if (this.state.reloading) {
      return <Loading />;
    }
    if (this.state.hasError) {
      // fallback UI
      return (
        <div className="text-center mt-5">
          <h1>Sorry, something went wrong.</h1>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ErrorBoundary: FC<{ children: ReactNode }> = ({ children }) => {
  const currentUser = useAppSelector(
    (state) => state.auth.currentUser?.data as { email: string; id: number }
  );
  return (
    <ErrorBoundaryClass currentUser={currentUser}>{children}</ErrorBoundaryClass>
  );
};

export default ErrorBoundary;
